import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DragMoveEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  type DropAnimation,
  PointerSensor,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, type UseSortableArguments } from '@dnd-kit/sortable';

import { useVirtualRows } from '@/hooks/use-virtual-rows';
import { getDocument } from '@/lib/utils';
import { SortableTreeItem } from './sortable-tree-item';
import { customListSortingStrategy } from './sorting-strategy';
import type {
  ItemChangedReason,
  SensorContext,
  TreeItem,
  TreeItemComponentType,
  TreeItems,
} from './types';
import {
  buildTree,
  flattenTree,
  getChildCount,
  getProjection,
  removeChildrenOf,
} from './utilities';

export type SortableTreeProps<TData extends object, TElement extends HTMLElement> = {
  items: TreeItems<TData>;
  onItemsChanged(items: TreeItems<TData>, reason: ItemChangedReason<TData>): void;
  TreeItemComponent: TreeItemComponentType<TData, TElement>;
  indentationWidth?: number;
  dropAnimation?: DropAnimation | null;
  sortableProps?: Omit<UseSortableArguments, 'id'>;
};

export function SortableTree<
  TreeItemData extends object,
  TElement extends HTMLElement = HTMLDivElement,
>({
  items,
  indentationWidth = 20,
  onItemsChanged,
  TreeItemComponent,
  dropAnimation,
  sortableProps,
  ...rest
}: SortableTreeProps<TreeItemData, TElement>) {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [overId, setOverId] = useState<UniqueIdentifier | null>(null);
  const [offsetLeft, setOffsetLeft] = useState(0);

  // Latest drag X lives in a ref; React state updates at most once per frame.
  const offsetLeftRef = useRef(0);
  const offsetRafRef = useRef<number | null>(null);

  const flattenedItems = useMemo(() => {
    const flattenedTree = flattenTree(items);
    const collapsedItems = flattenedTree
      .filter(({ children, collapsed }) => collapsed && children?.length)
      .map(({ id }) => id);

    return removeChildrenOf(
      flattenedTree,
      activeId ? [activeId, ...collapsedItems] : collapsedItems,
    );
  }, [activeId, items]);

  const projected = useMemo(
    () => getProjection(flattenedItems, activeId, overId, offsetLeft, indentationWidth),
    [flattenedItems, activeId, overId, offsetLeft, indentationWidth],
  );

  const sensorContext: SensorContext<TreeItemData> = useRef({
    items: flattenedItems,
    offset: offsetLeft,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
  );

  const sortedIds = useMemo(() => flattenedItems.map(({ id }) => id), [flattenedItems]);

  const activeItem = useMemo(
    () => (activeId ? flattenedItems.find(({ id }) => id === activeId) : null),
    [activeId, flattenedItems],
  );

  const { listRef, virtualizer } = useVirtualRows({
    count: flattenedItems.length,
    getItemKey: (index) => String(flattenedItems[index]?.id ?? index),
  });

  useEffect(() => {
    sensorContext.current = {
      items: flattenedItems,
      offset: offsetLeft,
    };
  }, [flattenedItems, offsetLeft]);

  const cancelOffsetRaf = useCallback(() => {
    if (offsetRafRef.current == null) return;
    cancelAnimationFrame(offsetRafRef.current);
    offsetRafRef.current = null;
  }, []);

  const strategyCallback = useCallback(() => !!projected, [projected]);

  function handleDragStart({ active: { id: dragActiveId } }: DragStartEvent) {
    offsetLeftRef.current = 0;
    setActiveId(dragActiveId);
    setOverId(dragActiveId);
    setOffsetLeft(0);
    getDocument()?.body.style.setProperty('cursor', 'grabbing');
  }

  function handleDragMove({ delta }: DragMoveEvent) {
    offsetLeftRef.current = delta.x;
    sensorContext.current.offset = delta.x;

    if (offsetRafRef.current != null) return;

    offsetRafRef.current = requestAnimationFrame(() => {
      offsetRafRef.current = null;
      setOffsetLeft(offsetLeftRef.current);
    });
  }

  function handleDragOver({ over }: DragOverEvent) {
    setOverId(over?.id ?? null);
  }

  function handleDragEnd({ active, over }: DragEndEvent) {
    cancelOffsetRaf();
    const finalOffset = offsetLeftRef.current;
    const finalProjected = getProjection(
      flattenedItems,
      activeId,
      overId,
      finalOffset,
      indentationWidth,
    );
    resetState();

    if (!finalProjected || !over) return;

    const { depth, parentId } = finalProjected;

    const allFlattenedItems = flattenTree(items);
    const draggedItemIndex = allFlattenedItems.findIndex(({ id }) => id === active.id);
    const targetItemIndex = allFlattenedItems.findIndex(({ id }) => id === over.id);

    if (draggedItemIndex === -1 || targetItemIndex === -1) return;

    const draggedItem = allFlattenedItems[draggedItemIndex];
    const originalParent = draggedItem.parent;
    allFlattenedItems[draggedItemIndex] = { ...draggedItem, depth, parentId };

    const reorderedItems = arrayMove(allFlattenedItems, draggedItemIndex, targetItemIndex);
    const newTreeItems = buildTree(reorderedItems);

    const finalDraggedItem = reorderedItems.find((item) => item.id === active.id);
    if (!finalDraggedItem) return;

    const findTreeItem = (
      treeItems: TreeItems<TreeItemData>,
      id: UniqueIdentifier,
    ): TreeItem<TreeItemData> | null => {
      for (const item of treeItems) {
        if (item.id === id) return item;
        if (item.children) {
          const found = findTreeItem(item.children, id);
          if (found) return found;
        }
      }
      return null;
    };

    const draggedTreeItem = findTreeItem(newTreeItems, active.id);
    const droppedToParentTreeItem = finalDraggedItem.parentId
      ? findTreeItem(newTreeItems, finalDraggedItem.parentId)
      : null;
    const draggedFromParentTreeItem = originalParent
      ? findTreeItem(newTreeItems, originalParent.id)
      : null;

    if (!draggedTreeItem) return;

    onItemsChanged(newTreeItems, {
      type: 'dropped',
      draggedItem: draggedTreeItem,
      draggedFromParent: draggedFromParentTreeItem,
      droppedToParent: droppedToParentTreeItem,
    });
  }

  function resetState() {
    cancelOffsetRaf();
    offsetLeftRef.current = 0;
    setOverId(null);
    setActiveId(null);
    setOffsetLeft(0);
    getDocument()?.body.style.setProperty('cursor', '');
  }

  const virtualItems = virtualizer.getVirtualItems();
  const doc = getDocument();
  const dragOverlay =
    doc?.body && activeId && activeItem
      ? createPortal(
          <DragOverlay dropAnimation={dropAnimation ?? null}>
            <TreeItemComponent
              {...rest}
              item={activeItem}
              depth={activeItem.depth}
              clone
              childCount={getChildCount(items, activeId) + 1}
              indentationWidth={indentationWidth}
              isLast={false}
              parent={activeItem.parent}
              isOver={false}
              isOverParent={false}
            />
          </DragOverlay>,
          doc.body,
        )
      : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      // Virtual rows remount as you scroll; compensating layout shifts fights the virtualizer.
      autoScroll={{ layoutShiftCompensation: false }}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={resetState}
    >
      <SortableContext items={sortedIds} strategy={customListSortingStrategy(strategyCallback)}>
        <div
          ref={listRef}
          className="relative w-full"
          style={{ height: virtualizer.getTotalSize() }}
        >
          {virtualItems.map((virtualRow) => {
            const item = flattenedItems[virtualRow.index];
            if (!item) return null;

            const isActive = item.id === activeId;
            const itemProjected = isActive && projected ? projected : null;

            return (
              <div
                key={item.id}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                className="absolute top-0 left-0 w-full"
                style={{
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                <SortableTreeItem
                  {...rest}
                  id={item.id as string}
                  item={item}
                  childCount={item.children?.length}
                  depth={itemProjected?.depth ?? item.depth}
                  indentationWidth={indentationWidth}
                  collapsed={Boolean(item.collapsed && item.children?.length)}
                  isLast={itemProjected?.isLast ?? item.isLast}
                  parent={itemProjected?.parent ?? item.parent}
                  TreeItemComponent={TreeItemComponent}
                  sortableProps={sortableProps}
                />
              </div>
            );
          })}
        </div>
        {dragOverlay}
      </SortableContext>
    </DndContext>
  );
}
